import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tagOptions, setTagOptions] = useState([]);

  const load = async () => {
    const [clientsResp, tagsResp] = await Promise.all([
      api.get("/admin/clients"),
      api.get("/admin/client-tags"),
    ]);
    setClients(clientsResp.data || []);
    setTagOptions(tagsResp.data?.tags || []);
  };

  const recalculateLevels = async () => {
    try {
      const { data } = await api.post("/admin/clients/recalculate-levels");
      toast.success(`Niveles recalculados: ${data.updated} cliente(s)`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (c) => {
    setSelected(c);
    const { data } = await api.get(`/admin/clients/${c.id}`);
    setDetail(data);
  };

  const update = async (id, patch, label) => {
    try {
      await api.patch(`/admin/clients/${id}`, patch);
      toast.success(label || "Cliente actualizado");
      await load();
      if (selected?.id === id) {
        setSelected({ ...selected, ...patch });
        if (detail) setDetail({ ...detail, client: { ...detail.client, ...patch } });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleTag = async (client, tag) => {
    const tags = Array.isArray(client.client_tags) ? client.client_tags : [];
    const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
    await update(client.id, { client_tags: next }, "Etiquetas actualizadas");
  };

  const typeLabel = {
    normal: "Normal",
    estudiante: "Estudiante",
    empresa: "Empresa",
    vip: "VIP",
    preferente: "Preferente",
  };

  const filtered = clients.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  const levelInfo = (level) => {
    const l = level || "Bronce";
    const map = {
      Bronce: { icon: "🟤", cls: "bg-amber-100 text-amber-900 border-amber-200" },
      Plata: { icon: "⚪", cls: "bg-slate-100 text-slate-800 border-slate-200" },
      Oro: { icon: "🟡", cls: "bg-yellow-100 text-yellow-900 border-yellow-200" },
      Platino: { icon: "🔷", cls: "bg-blue-100 text-blue-900 border-blue-200" },
      Diamante: { icon: "💎", cls: "bg-cyan-100 text-cyan-900 border-cyan-200" },
      Histórico: { icon: "🏛️", cls: "bg-purple-100 text-purple-900 border-purple-200" },
    };
    return map[l] || map.Bronce;
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Clientes</h1>
          <p className="text-sm text-slate-600">Marca como “fiable” a quien permitas “preparado para recoger”.</p>
        </div>
        <Button variant="outline" onClick={recalculateLevels}>
          Recalcular niveles
        </Button>
      </div>

      <Input
        data-testid="search-clients"
        placeholder="Buscar nombre o email…"
        value={search} onChange={(e) => setSearch(e.target.value)}
        className="mb-4 h-9 max-w-xs"
      />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="p-3">Nombre</th>
              <th className="p-3">Email / Teléfono</th>
              <th className="p-3">Pedidos</th>
              <th className="p-3">Nivel</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Fiable</th>
              <th className="p-3">Recogida preparada</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-slate-500">Sin clientes.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-bold text-slate-900">{c.name}</td>
                <td className="p-3 text-xs text-slate-600">
                  <div>{c.email}</div>
                  <div>{c.phone}</div>
                </td>
                <td className="p-3">
                  <Badge variant="outline">{c.orders_count}</Badge>
                </td>
                <td className="p-3">
                  {(() => {
                    const info = levelInfo(c.client_level);
                    return (
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-black ${info.cls}`}>
                        <span>{info.icon}</span>
                        <span>{c.client_level || "Bronce"}</span>
                      </span>
                    );
                  })()}
                </td>
                <td className="p-3">
                  <Badge variant="outline">{typeLabel[c.client_type || "normal"] || "Normal"}</Badge>
                </td>
                <td className="p-3">
                  <Switch
                    data-testid={`toggle-reliable-${c.id}`}
                    checked={!!c.is_reliable}
                    onCheckedChange={(v) => update(c.id, { is_reliable: v }, v ? "Marcado fiable" : "Quitado fiable")}
                  />
                </td>
                <td className="p-3">
                  <Switch
                    data-testid={`toggle-pickup-${c.id}`}
                    checked={!!c.allow_pickup_ready}
                    onCheckedChange={(v) => update(c.id, { allow_pickup_ready: v })}
                    disabled={!c.is_reliable}
                  />
                </td>
                <td className="p-3 text-right">
                  <Button
                    data-testid={`view-client-${c.id}`}
                    variant="outline" size="sm"
                    onClick={() => openDetail(c)}
                  >
                    Ver
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-slate-700">
                <div>{selected.email} · {selected.phone}</div>
              </div>
              <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-3">
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Nivel KAPEI</div>
                  <div className="mt-1 text-xl font-black text-[#4d7c0f]">
                    {detail?.client?.effective_client_level || detail?.client?.manual_client_level || detail?.client?.client_level || selected.effective_client_level || selected.manual_client_level || selected.client_level || "Bronce"}
                  </div>
                  {(detail?.client?.manual_client_level || selected.manual_client_level) && (
                    <div className="mt-1 text-xs font-bold text-purple-700">
                      Nivel manual aplicado
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Pedidos acumulados</div>
                  <div className="mt-1 text-xl font-black text-slate-900">
                    {detail?.client?.lifetime_orders ?? selected.lifetime_orders ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Páginas acumuladas</div>
                  <div className="mt-1 text-xl font-black text-slate-900">
                    {detail?.client?.lifetime_pages ?? selected.lifetime_pages ?? 0}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase text-slate-500">Notas internas</label>
                <Textarea
                  data-testid="client-notes"
                  defaultValue={selected.internal_notes || ""}
                  onBlur={(e) => update(selected.id, { notes: e.target.value }, "Notas guardadas")}
                  className="mt-2 min-h-[100px]"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>Nivel manual</Label>
                  <Select
                    value={detail?.client?.manual_client_level || selected.manual_client_level || "auto"}
                    onValueChange={(v) => update(selected.id, { manual_client_level: v === "auto" ? "" : v }, "Nivel manual actualizado")}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Nivel manual" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automático</SelectItem>
                      <SelectItem value="Bronce">Bronce</SelectItem>
                      <SelectItem value="Plata">Plata</SelectItem>
                      <SelectItem value="Oro">Oro</SelectItem>
                      <SelectItem value="Platino">Platino</SelectItem>
                      <SelectItem value="Diamante">Diamante</SelectItem>
                      <SelectItem value="Histórico">Histórico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Tipo de cliente</Label>
                  <Select
                    value={detail?.client?.client_type || selected.client_type || "normal"}
                    onValueChange={(v) => update(selected.id, { client_type: v }, "Tipo de cliente actualizado")}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Tipo de cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="estudiante">Estudiante</SelectItem>
                      <SelectItem value="empresa">Empresa</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                      <SelectItem value="preferente">Preferente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Etiquetas</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tagOptions.map((tag) => {
                      const current = detail?.client || selected;
                      const active = Array.isArray(current.client_tags) && current.client_tags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(current, tag)}
                          className={`rounded-full border px-3 py-1 text-xs font-bold ${
                            active ? "border-[#4d7c0f] bg-[#4d7c0f] text-white" : "border-slate-200 bg-white text-slate-600"
                          }`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-bold uppercase text-slate-500">Historial</div>
                <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
                  {(detail?.orders || []).map((o) => (
                    <li key={o.id} className="flex justify-between rounded border border-slate-100 p-2">
                      <span className="font-mono font-bold text-[#4d7c0f]">{o.code}</span>
                      <span className="text-xs text-slate-600">{o.status}</span>
                      <span className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("es-ES")}</span>
                    </li>
                  ))}
                  {detail && detail.orders.length === 0 && (
                    <li className="text-xs italic text-slate-500">Sin pedidos.</li>
                  )}
                </ul>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
